
import { computed } from 'vue';
import { VoiceAnalysisStatus, ZoomPhoneResultStatus } from '@/api/openapi';
import { ZoomPhoneResultApiService } from '@/api/user/resources/zoom_phone_result';
import { useAutoVoiceAnalysis } from './auto-voice-analysis';
import { useManualVoiceAnalysis } from './manual-voice-analysis';
import type { VoiceAnalysisManagerOptions } from './voice-analysis-types';

// 型の再エクスポート
export type { TargetTask, VoiceAnalysisManagerOptions } from './voice-analysis-types';

/**
 * 統合音声分析マネージャー
 * - 手動分析と自動分析を統合
 */
export const useVoiceAnalysisManager = (options?: VoiceAnalysisManagerOptions) => {
  const api = new ZoomPhoneResultApiService();

  const manual = useManualVoiceAnalysis(options);
  const auto = useAutoVoiceAnalysis(options);


  const analyzingIds = computed(() => {
    const combined = new Set<number>();
    manual.analyzingIds.value.forEach(id => combined.add(id));
    auto.analyzingIds.value.forEach(id => combined.add(id));
    return combined;
  });

  const isAnalyzing = (id: number): boolean => {
    return manual.isAnalyzing(id) || auto.isAnalyzing(id);
  };

  const stopAll = () => {
    manual.stopAll();
    auto.stopAll();
  };

  /**
   * 全タスクを取得して、手動・自動に振り分ける
   */
  const resumePollingForResults = async () => {
    const response = await api.transcriptionStatusesZoomPhoneResults({
      request: { all: true },
    });

    const data = response.data;
    const statuses = data.statuses;

    const processingStatuses = statuses.filter(
      (status: ZoomPhoneResultStatus) => status.voiceAnalysisStatus === VoiceAnalysisStatus.Processing,
    );

    const newStatuses = processingStatuses.filter(
      (status: ZoomPhoneResultStatus) => !analyzingIds.value.has(status.id),
    );

    // executeMethod に基づいて手動・自動に振り分ける
    newStatuses.forEach((status: ZoomPhoneResultStatus) => {
      const calleeDidNumber = status.calleeDidNumber || undefined;

      if (status.executeMethod === 'manual') {
        // 手動分析: バナー表示で復帰
        manual.restoreAnalysis(status.id, calleeDidNumber);
      } else if (status.executeMethod === 'automatic') {
        // 自動分析: バナーなしで追跡
        auto.trackAnalysis(status.id, calleeDidNumber);
      }
    });
  };

  return {
    // 統合された状態
    analyzingIds,
    isAnalyzing,
    stopAll,
    resumePollingForResults,

    // 手動分析専用
    bannerStates: manual.bannerStates,
    startAnalysis: manual.startAnalysis,
    dismissBanner: manual.dismissBanner,

    // 自動分析専用
    trackAnalysis: auto.trackAnalysis,
  };
};
